import axios from 'axios';
import type { LoginRequest, LoginResponse, User } from '@/types/auth';
import { API_BASE_URL } from '@/lib/config';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json', 
  },
});

class AuthService {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('token');
    if (this.token) {
      this.setAuthHeader(this.token);
    }
  }

  // Set authorization header for all requests
  private setAuthHeader(token: string) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  // Login user and store token
  async login(username: string, password: string): Promise<LoginResponse> {
    const request: LoginRequest = {
      username,
      password,
    };

    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', request);
      const { token } = response.data;
      
      // Store token and set header
      this.token = token;
      localStorage.setItem('token', token);
      this.setAuthHeader(token);

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    await apiClient.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, password: string, confirmPassword: string): Promise<void> {
    await apiClient.post('/auth/reset-password', { token, password, confirmPassword });
  }

  // Logout user and remove token
  logout(): void {
    this.token = null;
    localStorage.removeItem('token');
    delete apiClient.defaults.headers.common['Authorization'];
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.token;
  }

  // Get current token
  getToken(): string | null {
    return this.token;
  }

  // Get current user info (you might need to implement a getUser endpoint)
  async getCurrentUser(): Promise<User | null> {
    if (!this.token) {
      return null;
    }

    try {
      // This would require a getUser endpoint in your API
      // For now, we can decode the token to get basic info
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      return {
        id: payload.userId || payload.nameid,
        username: payload.name || payload.unique_name,
        email: payload.email || '',
        firstName: payload.firstName || payload.given_name || '',
        lastName: payload.lastName || payload.family_name || '',
        role: payload.role || 'Student',
        createdAt: new Date().toISOString(),
        isActive: true,
      };
    } catch (error) {
      return null;
    }
  }
}

export const authService = new AuthService();

export default authService;
